import { useState, useCallback } from 'react'
import { useAuth } from '@/context/AuthContext'
import { supabase } from '@/integrations/supabase/client'
import { getCurrentSeasonForCompetition } from '@/lib/season'
import { COMPETITIONS_LIST, type CompetitionConfig } from '@/lib/competitions'
import { invokeEdgeFunction } from '@/lib/edge-functions'

export type RegistrationStep = 'account' | 'details' | 'competition' | 'payment' | 'invite' | 'complete'

const STORAGE_KEY = 'socrates-register-league'

export interface LeagueFormData {
  name: string
  competitionCode: string
  season: string
}

interface StoredState {
  leagueId: string
  slug: string
  formData?: LeagueFormData
}

export function useRegisterLeague() {
  const { user, isSuperAdmin, refreshMemberships, setCurrentMembership } = useAuth()

  const [step, setStep] = useState<RegistrationStep>(() => {
    if (!user) return 'account'

    // Check for Stripe return URL params to avoid flashing the wrong step
    const params = new URLSearchParams(window.location.search)
    if (params.get('cancelled') === 'true') return 'payment'
    if (params.get('session_id') || params.get('league_id')) return 'invite'

    return 'details'
  })

  const [formData, setFormData] = useState<LeagueFormData>(() => {
    // Restore form data from localStorage if returning from Stripe redirect
    const params = new URLSearchParams(window.location.search)
    if (params.get('cancelled') === 'true' || params.get('session_id') || params.get('league_id')) {
      try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
          const parsed = JSON.parse(stored)
          if (parsed.formData) return { name: '', competitionCode: '', season: '', ...parsed.formData }
        }
      } catch { /* ignore */ }
    }
    return { name: '', competitionCode: '', season: '' }
  })

  const [leagueId, setLeagueId] = useState<string | null>(() => {
    // Restore leagueId from URL params / localStorage on Stripe return
    // so it's available immediately (matches step initializer above)
    const params = new URLSearchParams(window.location.search)
    const fromUrl = params.get('league_id')
    if (fromUrl) return fromUrl

    if (params.get('cancelled') === 'true' || params.get('session_id')) {
      try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
          const parsed = JSON.parse(stored)
          if (parsed.leagueId) return parsed.leagueId
        }
      } catch { /* ignore */ }
    }
    return null
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateFormData = useCallback((updates: Partial<LeagueFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
  }, [])

  // Persist league ID to localStorage before Stripe redirect
  const persistState = useCallback((state: StoredState) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  }, [])

  // Restore league ID from localStorage after Stripe redirect
  const restoreState = useCallback((): StoredState | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        return JSON.parse(stored)
      }
    } catch {
      // ignore parse errors
    }
    return null
  }, [])

  const clearStoredState = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  // Called when user completes step 0 (account creation) and is now authenticated.
  // Check for Stripe return params so re-auth after expired session goes to the right step.
  // Also restores leagueId and formData from URL/localStorage since the useEffect in
  // Register.tsx can't reliably catch the step === 'account' → authenticated transition.
  const onAccountCreated = useCallback(() => {
    const params = new URLSearchParams(window.location.search)
    const returnedLeagueId = params.get('league_id')
    const isStripeReturn = params.get('cancelled') === 'true' || params.get('session_id') || returnedLeagueId

    // Restore leagueId and formData from URL/localStorage for Stripe returns
    if (isStripeReturn) {
      const stored = restoreState()
      const resolvedLeagueId = returnedLeagueId || stored?.leagueId
      if (resolvedLeagueId) {
        setLeagueId(resolvedLeagueId)
      }
      if (stored?.formData) {
        setFormData(prev => ({ ...prev, ...stored.formData! }))
      }
    }

    if (params.get('cancelled') === 'true') {
      setStep('payment')
      return
    }
    if (params.get('session_id') || returnedLeagueId) {
      setStep('invite')
      return
    }
    setStep('details')
  }, [restoreState])

  // Called when step 2 is done — create the league and either redirect to Stripe or skip to invites.
  // If a league was already created (e.g. payment cancelled and retrying), reuse it.
  const createLeague = useCallback(async (options?: { paymentRequired?: boolean }) => {
    if (!formData.name || !formData.competitionCode) {
      setError('Please fill in all required fields')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Check for an existing pending league from a previous attempt
      const existingLeagueId = leagueId || restoreState()?.leagueId

      const requestBody = {
        name: formData.name,
        competitionCode: formData.competitionCode,
        season: formData.season || undefined,
        existingLeagueId: existingLeagueId || undefined,
      }

      const { data, error: fnError } = await invokeEdgeFunction('create-league', {
        body: requestBody,
      })

      if (fnError) {
        throw new Error(fnError.message)
      }

      if (!data?.leagueId) {
        throw new Error('No league ID returned')
      }

      setLeagueId(data.leagueId)
      persistState({ leagueId: data.leagueId, slug: data.slug, formData })

      if (options?.paymentRequired && !data.paymentWaived) {
        setStep('payment')
      } else {
        setStep('invite')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create league'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [formData, leagueId, persistState, restoreState])

  // Send batch invites
  const sendInvites = useCallback(async (emails: string[]) => {
    const targetLeagueId = leagueId
    if (!user) return
    if (!targetLeagueId) {
      setError('League not found. Please go back and try creating your league again.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Filter out creator's own email and deduplicate
      const uniqueEmails = [...new Set(
        emails
          .map(e => e.trim().toLowerCase())
          .filter(e => e && (!user.email || e !== user.email.toLowerCase()))
      )]

      let sentCount = 0
      for (const email of uniqueEmails) {
        // Insert invite record
        const { data: invite, error: inviteError } = await supabase
          .from('league_invites')
          .insert({
            league_id: targetLeagueId,
            email,
            invited_by: user.id,
            role: 'member',
          })
          .select('id')
          .single()

        if (inviteError) {
          console.error(`Failed to create invite for ${email}:`, inviteError)
          continue
        }

        sentCount++

        // Send email (fire-and-forget — don't block on email failures)
        invokeEdgeFunction('send-invite-email', {
          body: { inviteId: invite.id },
        }).catch(err => {
          console.error(`Failed to send invite email to ${email}:`, err)
        })
      }

      if (sentCount === 0 && uniqueEmails.length > 0) {
        setError('Failed to send invites. Please try again.')
        return
      }

      setStep('complete')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send invites'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [leagueId, user])

  // Finalize: refresh memberships and navigate to dashboard.
  // Retries a few times if the new league hasn't appeared yet (Stripe webhook delay).
  const finalize = useCallback(async () => {
    setLoading(true)
    clearStoredState()
    const targetLeagueId = leagueId

    try {
      const MAX_ATTEMPTS = 5
      const RETRY_DELAY_MS = 2000

      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        const freshMemberships = await refreshMemberships()

        if (targetLeagueId) {
          const newMembership = freshMemberships.find(m => m.league_id === targetLeagueId)
          if (newMembership) {
            setCurrentMembership(newMembership)
            return
          }
          // League not in memberships yet — wait and retry
          if (attempt < MAX_ATTEMPTS - 1) {
            await new Promise(r => setTimeout(r, RETRY_DELAY_MS))
          }
        } else {
          // No specific league to wait for — pick the most recent
          if (freshMemberships.length > 0) {
            setCurrentMembership(freshMemberships[freshMemberships.length - 1])
          }
          return
        }
      }

      // Exhausted retries — select most recent membership as best-effort fallback
      const fallback = await refreshMemberships()
      if (fallback.length > 0) {
        setCurrentMembership(fallback[fallback.length - 1])
      }
    } finally {
      setLoading(false)
    }
  }, [leagueId, clearStoredState, refreshMemberships, setCurrentMembership])

  return {
    step,
    setStep,
    formData,
    updateFormData,
    leagueId,
    setLeagueId,
    loading,
    error,
    setError,
    isSuperAdmin,
    user,
    onAccountCreated,
    createLeague,
    sendInvites,
    finalize,
    restoreState,
    clearStoredState,
  }
}

/**
 * Get the current season string for a competition.
 * Delegates to the canonical implementation in src/lib/season.ts.
 */
export function getCurrentSeason(comp: CompetitionConfig): string {
  return getCurrentSeasonForCompetition(comp.seasonFormat)
}

